/**
 * Shared calendar day defaults (WO / PH / working-day fallback).
 * Source of truth matches Admin Attendance.jsx getDefaultStatus* so ESS and
 * Admin calendars paint the same cells for the same employee/month.
 */
import { isEmployeeManualAttendanceProcess } from "./attendancePolicyEngine";
import { getWorkingDayDefaultStatus } from "./attendanceGeneralSettings";
import {
  hasApprovedChangeWoForDay,
  hasApprovedProposedWoForDay,
} from "./weeklyOffChange";

function parseDateValue(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isPolicyEffectiveOnDate(policy, targetDate) {
  if (!policy) return false;
  const effectiveDate = parseDateValue(policy.effectiveDate);
  if (!effectiveDate) return true;
  return effectiveDate <= targetDate;
}

function buildEmployeeKeyCandidates(employeeContext) {
  const candidates = new Set();
  const addValue = (value) => {
    if (value === undefined || value === null) return;
    if (typeof value === "number") {
      if (!Number.isNaN(value)) candidates.add(String(value));
      return;
    }
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return;
      candidates.add(trimmed);
      candidates.add(trimmed.toLowerCase());
      candidates.add(trimmed.toUpperCase());
    }
  };

  if (employeeContext && typeof employeeContext === "object") {
    addValue(employeeContext.id);
    addValue(employeeContext.employeeId);
    addValue(employeeContext.empId);
    addValue(employeeContext.empCode);
    addValue(employeeContext.code);
    addValue(employeeContext.employeeCode);
    addValue(employeeContext.user?.employeeCode);
  } else {
    addValue(employeeContext);
  }

  return Array.from(candidates).filter((value) => value !== "");
}

function resolveEmployeePolicyAssignment(employeeContext, mappings = {}) {
  if (!mappings || typeof mappings !== "object") return null;
  const candidates = buildEmployeeKeyCandidates(employeeContext);
  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(mappings, key)) {
      return mappings[key];
    }
  }
  return null;
}

/** Same shape normalizer as Admin Attendance.jsx (policyId → policies[]). */
export function normalizeEmployeeWeeklyOffMapping(data = {}) {
  return Object.fromEntries(
    Object.entries(data || {}).map(([empId, mapping]) => {
      if (Array.isArray(mapping)) {
        return [
          empId,
          mapping.map((v) => Number(v)).filter((v) => !Number.isNaN(v)),
        ];
      }
      if (typeof mapping === "object" && mapping !== null) {
        if (mapping.policyId != null && !mapping.policies) {
          const normalizedPolicies = [Number(mapping.policyId)].filter(
            (v) => !Number.isNaN(v)
          );
          return [
            empId,
            {
              ...mapping,
              policies: normalizedPolicies,
              allowOverride:
                mapping.allowOverride === true ||
                mapping.allowOverride === "true",
              overridePolicy:
                mapping.overridePolicy === true ||
                mapping.overridePolicy === "true",
            },
          ];
        }
        const normalizedPolicies = Array.isArray(mapping.policies)
          ? mapping.policies
              .map((v) => Number(v))
              .filter((v) => !Number.isNaN(v))
          : mapping.policies;
        return [
          empId,
          {
            ...mapping,
            policies: normalizedPolicies,
            allowOverride:
              mapping.allowOverride === true ||
              mapping.allowOverride === "true",
            overridePolicy:
              mapping.overridePolicy === true ||
              mapping.overridePolicy === "true",
          },
        ];
      }
      const asNum = Number(mapping);
      return [empId, Number.isNaN(asNum) ? mapping : [asNum]];
    })
  );
}

export function normalizeWeeklyOffPolicies(policies = []) {
  return (Array.isArray(policies) ? policies : []).map((p) => ({
    ...p,
    id: typeof p.id === "string" ? Number(p.id) : p.id,
    overridePolicy: p.overridePolicy === true || p.overridePolicy === "true",
    allowOverride: p.allowOverride === true || p.allowOverride === "true",
  }));
}

function getEffectiveWeeklyOffPolicyForEmployee(
  date,
  employeeContext,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {}
) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  if (!Array.isArray(weeklyOffPolicies) || weeklyOffPolicies.length === 0) {
    return null;
  }

  const assigned = resolveEmployeePolicyAssignment(
    employeeContext,
    employeeWeeklyOffMappings
  );
  if (assigned) {
    let policyIds = Array.isArray(assigned)
      ? assigned
      : assigned?.policies || [];
    if (
      !policyIds.length &&
      assigned &&
      typeof assigned === "object" &&
      assigned.policyId != null
    ) {
      const pid = Number(assigned.policyId);
      if (!Number.isNaN(pid)) policyIds = [pid];
    }
    const policy = weeklyOffPolicies.find((p) =>
      policyIds.includes(typeof p.id === "string" ? Number(p.id) : p.id)
    );
    return policy || null;
  }
  return (
    weeklyOffPolicies.find(
      (p) => isPolicyEffectiveOnDate(p, date) && p.weeklyOffs
    ) || null
  );
}

function getAllowOverrideForEmployee(
  date,
  employeeContext,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  employeeIdForMapping
) {
  const empId =
    employeeIdForMapping ??
    (employeeContext && typeof employeeContext === "object"
      ? employeeContext.id || employeeContext.employeeId || employeeContext.code
      : employeeContext);
  const mapping =
    empId != null ? employeeWeeklyOffMappings?.[String(empId)] : null;

  if (mapping) {
    const policyIds = (
      Array.isArray(mapping) ? mapping : mapping?.policies || []
    )
      .map((v) => Number(v))
      .filter((n) => !Number.isNaN(n));
    const singlePolicyId =
      typeof mapping === "object" &&
      !Array.isArray(mapping) &&
      mapping.policyId != null
        ? Number(mapping.policyId)
        : null;
    const ids =
      policyIds.length > 0
        ? policyIds
        : singlePolicyId != null && !Number.isNaN(singlePolicyId)
          ? [singlePolicyId]
          : [];
    if (ids.length > 0) {
      const policy = weeklyOffPolicies.find((p) =>
        ids.includes(typeof p.id === "string" ? Number(p.id) : p.id)
      );
      return (
        policy != null &&
        (policy.allowOverride === true || policy.overridePolicy === true)
      );
    }
  }

  const effectivePolicy = getEffectiveWeeklyOffPolicyForEmployee(
    date,
    employeeContext,
    weeklyOffPolicies,
    employeeWeeklyOffMappings
  );
  return (
    effectivePolicy != null &&
    (effectivePolicy.allowOverride === true ||
      effectivePolicy.overridePolicy === true)
  );
}

export function isWeeklyOffForEmployee(
  date,
  employeeContext = null,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {}
) {
  try {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;

    const empId =
      employeeContext && typeof employeeContext === "object"
        ? employeeContext.id || employeeContext.employeeId
        : employeeContext;
    if (empId != null) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      const key = `${y}-${m}-${d}`;
      if (hasApprovedChangeWoForDay(empId, key)) return false;
      if (hasApprovedProposedWoForDay(empId, key)) return "full";
    }

    const dayName = date
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();

    const assigned = resolveEmployeePolicyAssignment(
      employeeContext,
      employeeWeeklyOffMappings
    );

    let matchedPolicy = null;
    if (assigned) {
      let policyIds = Array.isArray(assigned)
        ? assigned
        : assigned?.policies || [];
      if (
        !policyIds.length &&
        assigned &&
        typeof assigned === "object" &&
        assigned.policyId != null
      ) {
        const pid = Number(assigned.policyId);
        if (!Number.isNaN(pid)) policyIds = [pid];
      }
      matchedPolicy = weeklyOffPolicies.find((p) =>
        policyIds.includes(typeof p.id === "string" ? Number(p.id) : p.id)
      );
    }
    if (!matchedPolicy) {
      matchedPolicy = weeklyOffPolicies.find(
        (p) => isPolicyEffectiveOnDate(p, date) && p.weeklyOffs
      );
    }
    if (!matchedPolicy) return false;

    const weeklyOffConfig = matchedPolicy.weeklyOffs?.[dayName];
    if (!weeklyOffConfig || !weeklyOffConfig.enabled) return false;
    return weeklyOffConfig.type || "full";
  } catch {
    return false;
  }
}

function isWeeklyOff(date, weeklyOffPolicies = []) {
  try {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
    const dayName = date
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();
    if (!Array.isArray(weeklyOffPolicies)) return false;
    for (const p of weeklyOffPolicies) {
      if (!p) continue;
      if (!isPolicyEffectiveOnDate(p, date)) continue;
      const weeklyOffConfig = p.weeklyOffs?.[dayName];
      if (weeklyOffConfig && weeklyOffConfig.enabled) return true;
    }
    return false;
  } catch {
    return false;
  }
}

export function isHoliday(
  date,
  employeeId = null,
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayPolicyMappings = {}
) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
  const dateString =
    date.getFullYear() +
    "-" +
    String(date.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(date.getDate()).padStart(2, "0");

  const norm = (d) =>
    d && typeof d === "string" ? d.trim().slice(0, 10) : "";
  const holidayDateOf = (holiday) => {
    if (holiday == null) return "";
    if (typeof holiday === "string" || typeof holiday === "number") {
      return norm(String(holiday));
    }
    return norm(holiday.date || holiday.holidayDate || "");
  };
  const isRestrictedHoliday = (holiday) =>
    String(holiday?.type || holiday?.holidayType || "").toLowerCase() ===
    "restricted";

  if (
    (holidaysData || []).some(
      (holiday) =>
        holidayDateOf(holiday) === dateString &&
        holiday.isGlobal &&
        !isRestrictedHoliday(holiday)
    )
  ) {
    return true;
  }

  if (employeeId == null || employeeId === "") return false;

  const mapping =
    employeeHolidayPolicyMappings[employeeId] ||
    employeeHolidayPolicyMappings[String(employeeId)] ||
    [];
  const employeePolicyIds = Array.isArray(mapping) ? mapping : [];
  const employeePolicies = (holidayPolicies || []).filter((policy) =>
    employeePolicyIds.includes(policy.id)
  );

  return employeePolicies.some((policy) =>
    (policy.holidays || []).some((holiday) => {
      const hDate = holidayDateOf(holiday);
      if (hDate !== dateString) return false;
      if (typeof holiday === "object" && isRestrictedHoliday(holiday)) {
        return false;
      }
      return true;
    })
  );
}

/**
 * Same contract as Admin Attendance.jsx getDefaultStatusWithLock.
 */
export function getDefaultStatusWithLock(
  date,
  employeeContext = null,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayMappings = {}
) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return { status: "-", locked: true };
  }

  if (employeeContext && typeof employeeContext === "object") {
    const joinDate = employeeContext.joinDate || employeeContext.joiningDate;
    const relievedDate =
      employeeContext.relievedDate ||
      employeeContext.exitDate ||
      employeeContext.resignationDate ||
      employeeContext.relievingDate;

    if (joinDate) {
      const joinParts = String(joinDate).match(/(\d{4})-(\d{2})-(\d{2})/);
      if (joinParts) {
        const joinNum = parseInt(joinParts[1] + joinParts[2] + joinParts[3], 10);
        const dateNum = parseInt(
          date.getFullYear().toString() +
            String(date.getMonth() + 1).padStart(2, "0") +
            String(date.getDate()).padStart(2, "0"),
          10
        );
        if (dateNum < joinNum) {
          return { status: "-", locked: true };
        }
      }
    }

    if (relievedDate) {
      const rd = new Date(relievedDate);
      if (date > rd) {
        return { status: "-", locked: true };
      }
    }
  }

  const holidayEmployeeKey =
    employeeContext && typeof employeeContext === "object"
      ? employeeContext.id || employeeContext.employeeId || employeeContext.code
      : employeeContext;

  if (
    isHoliday(
      date,
      holidayEmployeeKey,
      holidaysData,
      holidayPolicies,
      employeeHolidayMappings
    )
  ) {
    return { status: "PH", locked: true };
  }

  if (employeeContext) {
    const weeklyOffType = isWeeklyOffForEmployee(
      date,
      employeeContext,
      weeklyOffPolicies,
      employeeWeeklyOffMappings
    );

    if (
      weeklyOffType === "full" ||
      weeklyOffType === "first_half" ||
      weeklyOffType === "second_half"
    ) {
      const statusMap = {
        full: "WO",
        first_half: "WOPR",
        second_half: "PRWO",
      };
      const defaultStatus = statusMap[weeklyOffType];
      const allowOverride = getAllowOverrideForEmployee(
        date,
        employeeContext,
        weeklyOffPolicies,
        employeeWeeklyOffMappings
      );
      return { status: defaultStatus, locked: !allowOverride };
    }
  }

  if (!employeeContext && isWeeklyOff(date, weeklyOffPolicies)) {
    return { status: "WO", locked: false };
  }

  const workingDefault =
    employeeContext && typeof employeeContext === "object"
      ? isEmployeeManualAttendanceProcess(employeeContext)
        ? "PR"
        : "LOP"
      : getWorkingDayDefaultStatus();
  return { status: workingDefault, locked: false };
}

export function getDefaultStatus(
  date,
  employeeContext = null,
  weeklyOffPolicies = [],
  employeeWeeklyOffMappings = {},
  holidaysData = [],
  holidayPolicies = [],
  employeeHolidayMappings = {}
) {
  return getDefaultStatusWithLock(
    date,
    employeeContext,
    weeklyOffPolicies,
    employeeWeeklyOffMappings,
    holidaysData,
    holidayPolicies,
    employeeHolidayMappings
  ).status;
}

/** Parse attendance date without UTC day-shift (IST-safe). */
export function getDayOfMonthSafe(dateValue) {
  if (dateValue == null || dateValue === "") return null;
  const isoMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) return parseInt(isoMatch[3], 10);
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;
  return d.getDate();
}

export function parseYearMonthDay(dateValue) {
  if (dateValue == null || dateValue === "") return null;
  const isoMatch = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return {
      year: parseInt(isoMatch[1], 10),
      month: parseInt(isoMatch[2], 10) - 1,
      day: parseInt(isoMatch[3], 10),
    };
  }
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return null;
  return {
    year: d.getFullYear(),
    month: d.getMonth(),
    day: d.getDate(),
  };
}
